import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useTenant } from './TenantContext';
import { useClient } from './ClientContext';
import apiService from '../services/api.service';
import { useToast } from './ToastContext';
import CryptoJS from 'crypto-js';

const GamificationContext = createContext(null);

export const GamificationProvider = ({ children }) => {
  const { tenant } = useTenant();
  const { client } = useClient();
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  
  // Keep ref updated
  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Decrypt encrypted data from API
   */
  const decryptObject = useCallback(async (encryptedString) => {
    try {
      if (!encryptedString) return null;
      
      // Use the encryption utility with the secret key
      const secretKey = process.env.REACT_APP_ENCRYPTION_KEY;
      if (!secretKey) {
        console.error('Secret key not found in environment variables');
        return null;
      }
      
      const bytes = CryptoJS.AES.decrypt(encryptedString, secretKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      
      if (!decrypted) return null;
      
      return JSON.parse(decrypted);
    } catch (error) {
      console.error('Decryption error:', error);
      return null;
    }
  }, []);

  /**
   * Pick reward for a campaign
   * POST /api/gamification/pick-reward
   */
  const pickReward = useCallback(async (clientId, campaignId) => {
    if (!campaignId || !tenant?.tenantid || !clientId) {
      setError('Missing required parameters');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const info = {
        campaignId: campaignId,
        tenantid: tenant.tenantid,
        clientid: clientId
      };

      const response = await apiService.post('/api/gamification/pick-reward', info);
      if (response?.encryptedData) {
        const decryptedData = await decryptObject(response.encryptedData);
        return decryptedData;
      }
      
      return response;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to pick reward';
      setError(errorMessage);
      showToastRef.current({ type: 'error', message: errorMessage });
      return null;
    } finally {
      setLoading(false);
    }
  }, [tenant.tenantid, decryptObject]);

  /**
   * Issue reward after game completion
   * POST /api/gamification/reward-issue
   */
  const issueReward = useCallback(async (clientId, rewardData) => {
    if (!rewardData || !tenant?.tenantid || !clientId) {
      setError('Missing required parameters');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // NOTE: v3 ClientContext exposes camelCased fields:
      // - client.clientId
      // - client.externalUserId
      // We prefer the function arg `clientId` (source of truth for caller),
      // but fall back to `client?.clientId` if needed.
      const resolvedClientId = clientId || client?.clientId;

      const info = {
        campaignId: rewardData.campaignId,
        tenantid: tenant.tenantid,
        transactionId: rewardData.transactionId,
        rewardAmount: rewardData.rewardAmount,
        clientid: resolvedClientId,
        analytics: {
          action: rewardData.action || "Game Completed",
          externaluserid: client?.externalUserId || "",
          tenantid: tenant?.tenantid,
          campaignid: rewardData.campaignId
        }
      };

      const response = await apiService.post('/api/gamification/reward-issue', info);
      
      if (response?.status === 200) {
        return true;
      }
      
      return false;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to issue reward';
      setError(errorMessage);
      showToastRef.current({ type: 'error', message: errorMessage });
      return false;
    } finally {
      setLoading(false);
    }
  }, [tenant?.tenantid, client?.clientId, client?.externalUserId]);

  /**
   * Get campaign reward options
   * GET /api/gamification/get-campaign-reward-options
   */
  const getCampaignRewardOptions = useCallback(async (campaignId) => {
    if (!campaignId || !tenant?.tenantid || !client?.clientId) {
      setError('Missing required parameters');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        tenantid: tenant.tenantid,
        clientid: client.clientId,
        campaignId: campaignId,
      };

      const response = await apiService.get('/api/gamification/get-campaign-reward-options', { params });
      
      return response;
    } catch (error) {
      const errorMessage = error?.message || 'Failed to get campaign reward options';
      setError(errorMessage);
      showToastRef.current({ type: 'error', message: errorMessage });
      return null;
    } finally {
      setLoading(false);
    }
  }, [tenant?.tenantid, client?.clientId]);

  const value = {
    loading,
    error,
    pickReward,
    issueReward,
    getCampaignRewardOptions,
    decryptObject,
  };

  return <GamificationContext.Provider value={value}>{children}</GamificationContext.Provider>;
};

export const useGamification = () => {
  const ctx = useContext(GamificationContext);
  if (!ctx) throw new Error('useGamification must be used within GamificationProvider');
  return ctx;
};

